import type { CSSProperties } from 'react'
import React from 'react'
import { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { colors } from '../theme/styles'

const Placeholder = (props: any) => (
  <components.Placeholder {...props}>
    {props.selectProps.placeholder}
  </components.Placeholder>
)

// the package does not export typings for theme props
// generic type definition taken from 'packages/react-select/src/styles.js'
interface StyleProps {
  [key: string]: any
}

const customStyles: StyleProps = {
  container: (provided: CSSProperties, state: any) => ({
    ...provided,
    width: state.selectProps.width,
  }),
  option: (provided: CSSProperties) => ({
    ...provided,
    color: colors.appDarkIndigo,
    fontSize: '14px',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    textAlign: 'left',
  }),
  menu: (provided: CSSProperties) => ({
    ...provided,
    marginTop: '6px',
    borderRadius: '5px',
    border: `solid 0.5px ${colors.appGreyFour}`,
    boxShadow: 'none',
    backgroundColor: colors.appWhite,
  }),
  menuList: (provided: CSSProperties) => ({
    ...provided,
  }),
  control: (provided: CSSProperties, state: any) => ({
    ...provided,
    alignItems: 'start',
    '&:hover': {
      borderColor: state.menuIsOpen ? colors.appClearBlue : colors.appGreyFour,
    },
    minHeight: state.selectProps.variant === 'compact' ? '35px' : '100px',
    maxHeight: state.selectProps.variant === 'compact' ? '150px' : 'initial',
    height: state.selectProps.variant === 'compact' ? 'auto' : '100px',
    margin: '0px',
    overflowY: 'auto',
    borderRadius: '5px',
    border: 'solid 0.5px',
    borderColor: state.menuIsOpen ? colors.appClearBlue : colors.appGreyThree,
    boxShadow: 'none',
    backgroundColor: colors.appWhite,
    fontSize: '14px',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    letterSpacing: 'normal',
    textAlign: 'left',
    color: state.menuIsOpen ? colors.appClearBlue : colors.appGreyThree,
  }),
  dropdownIndicator: (provided: CSSProperties, state: any) => ({
    ...provided,
    color: state.menuIsOpen ? colors.appSlate : colors.appGreyFour,
  }),
  indicatorSeparator: (provided: CSSProperties) => ({
    ...provided,
    color: colors.appGreyFour,
  }),
  multiValueLabel: (provided: CSSProperties) => ({
    ...provided,
    backgroundColor: colors.appClearBlue,
    verticleAlign: 'middle',
    fontStretch: 'normal',
    fontStyle: 'normal',
    letterSpacing: 'normal',
    lineHeight: '17px',
    color: colors.appWhite,
    borderBottomLeftRadius: '20px',
    borderTopLeftRadius: '20px',
    borderRight: 0,
    paddingLeft: '10px',
    paddingRight: '4px',
  }),
  multiValue: (provided: CSSProperties) => ({
    ...provided,
    backgroundColor: colors.appClearBlue,
    borderRadius: '20px',
    border: 'none',
  }),
  multiValueRemove: (provided: CSSProperties) => ({
    ...provided,
    backgroundColor: colors.appClearBlue,
    color: colors.appWhite,
    borderBottomRightRadius: '20px',
    borderTopRightRadius: '20px',
    borderLeft: 0,
    paddingRight: '6px',
  }),
  valueContainer: (provided: CSSProperties, state: any) => ({
    ...provided,
    padding: '2px',
    height: state.selectProps.variant === 'compact' ? '100%' : 'auto',
  }),
  clearIndicator: (provided: CSSProperties, state: any) =>
    state.selectProps.variant !== 'compact'
      ? {
          ...provided,
          display: 'block',
          margin: '8px 11px 8px 33px',
          fontSize: '14px',
          fontStretch: 'normal',
          fontStyle: 'normal',
          lineHeight: 'normal',
          letterSpacing: 'normal',
          color: colors.appBattleshipGrey,
          '&:hover': {
            color: colors.appSlate,
          },
        }
      : {
          margin: '7px 5px 0px 18px',
        },
}

const ReactSelectCreatable: React.FC<any> = (props): React.ReactElement => (
  <CreatableSelect
    {...props}
    ref={props.refCallback}
    styles={customStyles}
    variant={props.variant === 'compact' ? 'compact' : 'default'}
    maxMenuHeight={props?.maxMenuHeight}
    components={{
      Placeholder,
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
      ...props.components,
    }}
    isValidNewOption={(inputValue, selectValue, selectOptions) => {
      const exactValueExists = props?.skipExactMatch
        ? selectOptions.find((el: any) => el.value === inputValue.toLowerCase())
        : selectOptions.find((el: any) => el.value === inputValue)
      const valueIsNotEmpty = inputValue.trim().length
      return !exactValueExists && valueIsNotEmpty
    }}
  />
)

export default ReactSelectCreatable
